import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, ListChecks, FileDashed, Sparkle } from '@phosphor-icons/react';
import type { QuestionItem } from 'screens/settings/DueDiligenceCategoryEditor/types';
import { DueDiligenceAnswerType } from 'shared/types/due-diligence';

interface WizardStep2Props {
  question: QuestionItem;
  onUpdate: (updates: Partial<QuestionItem>) => void;
}

const ANSWER_TYPE_OPTIONS = [
  {
    type: DueDiligenceAnswerType.OPTIONS,
    label: 'Status vraag',
    description: 'Meerkeuze met status kleuren',
    icon: CheckCircle,
    color: 'moss',
  },
  {
    type: DueDiligenceAnswerType.SIMPLE_SELECT,
    label: 'Selectie vraag',
    description: 'Simpele selectie uit opties',
    icon: ListChecks,
    color: 'slate',
  },
  {
    type: DueDiligenceAnswerType.DOCUMENT_EXISTS,
    label: 'Document aanwezig',
    description: 'Check of document bestaat',
    icon: FileDashed,
    color: 'sky',
  },
  {
    type: DueDiligenceAnswerType.EXTRACTION,
    label: 'Extractie',
    description: 'AI extractie uit document',
    icon: Sparkle,
    color: 'violet',
  },
];

function generateFieldName(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .trim();
}

export function WizardStep2Configure({ question, onUpdate }: WizardStep2Props) {
  const handleDisplayNameChange = (value: string) => {
    const fieldName = generateFieldName(value);
    onUpdate({ displayName: value, fieldName });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Configureer vraag</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Geef de vraag een naam en kies het antwoordtype.
        </p>
      </div>

      <div className="space-y-4">
        {/* Question name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Vraagnaam <span className="text-destructive">*</span>
          </label>
          <Input
            value={question.displayName}
            onChange={(e) => handleDisplayNameChange(e.target.value)}
            placeholder="bijv. EPC Certificaat"
            className="w-full"
          />
          {question.fieldName && (
            <p className="text-xs text-muted-foreground">
              Veldnaam: <code className="bg-muted px-1 py-0.5 rounded">{question.fieldName}</code>
            </p>
          )}
        </div>

        {/* Help text */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Help tekst</label>
          <Textarea
            value={question.helpText || ''}
            onChange={(e) => onUpdate({ helpText: e.target.value })}
            placeholder="Uitleg voor de gebruiker..."
            rows={3}
            className="w-full resize-none"
          />
        </div>

        {/* Answer type selector */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">
            Antwoordtype <span className="text-destructive">*</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ANSWER_TYPE_OPTIONS.map((option) => {
              const isSelected = question.answerType === option.type;
              const Icon = option.icon;

              return (
                <button
                  key={option.type}
                  type="button"
                  onClick={() => onUpdate({ answerType: option.type })}
                  className={`
                    p-4 rounded-lg border-2 text-left transition-all
                    ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`
                        w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                        ${option.color === 'moss' ? 'bg-moss-100 text-moss-700' : ''}
                        ${option.color === 'slate' ? 'bg-slate-100 text-slate-700' : ''}
                        ${option.color === 'sky' ? 'bg-sky-100 text-sky-700' : ''}
                        ${option.color === 'violet' ? 'bg-violet-100 text-violet-700' : ''}
                      `}
                    >
                      <Icon weight="regular" className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground">{option.label}</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
